import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  House, Fingerprint, CheckCircle, XCircle, Warning, ArrowRight, User, Sparkle,
} from '@phosphor-icons/react';
import {
  startRegistration as wkStartRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';

import { apiBase } from '@/utils/apiBase';
const API = apiBase();

export default function InvitePage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/invites/public/${token}`);
        setInvite(res.data);
        setName(res.data.suggested_name || '');
      } catch (e) {
        setError(e.response?.data?.detail || 'This invite link is invalid');
      }
      setLoading(false);
    };
    fetchInvite();
  }, [token]);

  const handleAccept = async () => {
    if (!name.trim() || name.trim().length < 2) {
      setError('Please enter your name (at least 2 characters)');
      return;
    }
    if (!browserSupportsWebAuthn()) {
      setError('Your browser does not support passkeys. Try Chrome, Safari 16+, or a modern Android browser.');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      const beginRes = await axios.post(`${API}/invites/public/${token}/passkey/begin`, { name: name.trim() });
      const credential = await wkStartRegistration({ optionsJSON: beginRes.data });
      await axios.post(`${API}/invites/public/${token}/passkey/complete`, {
        name: name.trim(),
        credential,
      });
      setSuccess(true);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Something went wrong';
      setError(msg.includes('NotAllowed') ? 'Passkey enrollment cancelled. Please try again.' : msg);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
        <div className="text-center max-w-sm" data-testid="invite-error">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <XCircle size={36} weight="duotone" className="text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Invite unavailable</h1>
          <p className="text-sm text-neutral-400">{error}</p>
        </div>
      </div>
    );
  }

  if (invite?.consumed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
        <div className="text-center max-w-sm" data-testid="invite-already-used">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/10 flex items-center justify-center">
            <Warning size={36} weight="duotone" className="text-yellow-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Already used</h1>
          <p className="text-sm text-neutral-400">This invite has already been accepted. If you need a new one, ask the home admin.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ background: '#0A0A0A' }}>
        <div className="text-center max-w-sm relative z-10 animate-fade-up" data-testid="invite-success">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8E53] flex items-center justify-center"
            style={{ boxShadow: '0 12px 40px rgba(255,107,53,0.5)' }}>
            <CheckCircle size={42} weight="fill" className="text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
            Welcome, {name}! <span className="inline-block">🎉</span>
          </h1>
          <p className="text-sm text-neutral-300 leading-relaxed mb-5">
            Your passkey is set up. The home admin will receive a notification to approve you.
          </p>
          <div className="p-4 rounded-2xl bg-white/3 border border-white/8 text-left">
            <div className="flex items-start gap-3">
              <Sparkle size={18} weight="fill" className="text-[#FF6B35] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-white font-semibold mb-1">What's next?</p>
                <p className="text-xs text-neutral-400 leading-relaxed">
                  Once approved, just open HomeCommand and tap <span className="text-[#00E5FF] font-medium">Sign in with Passkey</span>. Face ID or fingerprint — that's it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-10 relative overflow-hidden" style={{ background: '#0A0A0A' }} data-testid="invite-page" data-no-theme-shim>
      <div className="max-w-md mx-auto relative z-10">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FF8E53] flex items-center justify-center"
            style={{ boxShadow: '0 6px 20px rgba(255,107,53,0.4)' }}>
            <House size={18} weight="fill" className="text-white" />
          </div>
          <div>
            <p className="text-base font-bold text-white leading-tight">HomeCommand</p>
            <p className="text-[9px] text-[#FF6B35] tracking-[0.2em] uppercase">AI House Manager</p>
          </div>
        </div>

        {/* Hero */}
        <div className="text-center mb-8 animate-fade-up">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-4">
            <Sparkle size={11} weight="fill" className="text-[#FF6B35]" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF6B35]">Family Invitation</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-2">
            You're invited to join the home
          </h1>
          {invite?.invited_by_name && (
            <p className="text-sm text-neutral-400">
              <span className="text-neutral-300 font-medium">{invite.invited_by_name}</span> wants you on HomeCommand
            </p>
          )}
        </div>

        {/* Card */}
        <div className="rounded-3xl p-6 mb-4"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}>

          <label className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-2 block font-semibold">
            Your Name
          </label>
          <div className="relative">
            <User size={16} weight="duotone" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="invite-name-input"
              placeholder="e.g., Apeksha"
              autoFocus
              className="w-full pl-10 pr-4 py-3 rounded-2xl bg-black/40 border border-white/8 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#FF6B35]/60 transition-colors"
            />
          </div>
          <p className="text-[10px] text-neutral-500 mt-2">
            This is how you'll appear to the rest of the household.
          </p>

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400">{error}</div>
          )}

          <button
            onClick={handleAccept}
            disabled={submitting || !name.trim()}
            data-testid="invite-create-passkey-btn"
            className="w-full mt-5 py-3.5 rounded-full text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] active:scale-[0.99]"
            style={{
              background: 'linear-gradient(135deg, #FF6B35 0%, #FF8E53 100%)',
              boxShadow: '0 8px 24px rgba(255,107,53,0.4)',
            }}>
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Setting up your passkey...
              </>
            ) : (
              <>
                <Fingerprint size={18} weight="duotone" />
                Set up Passkey
                <ArrowRight size={14} weight="bold" />
              </>
            )}
          </button>

          <p className="text-[10px] text-neutral-500 mt-4 text-center leading-relaxed">
            Use Face ID, Touch ID, Windows Hello, or your phone's fingerprint. <br />
            No password needed — ever.
          </p>
        </div>

        {/* Why it's safe */}
        <div className="rounded-2xl p-4 mt-3" style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.15)' }}>
          <div className="flex items-start gap-2.5">
            <Sparkle size={14} weight="fill" className="text-[#00E5FF] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[11px] text-[#00E5FF] font-semibold mb-1 uppercase tracking-wider">Why this is safer</p>
              <p className="text-[11px] text-neutral-400 leading-relaxed">
                Your passkey lives only on this device's secure hardware. Even if someone steals this link, they can't sign in as you.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fade-up 0.5s ease-out forwards; }
      `}</style>
    </div>
  );
}
